import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { ArrowLeft, CalendarDays, ContactRound, MapPin, Navigation, Scissors, Send, Star, X } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import "./PublicMasterProfile.css";
import { BaseService } from "../../Base/BaseService";
import { PublicMasterProfileStore } from "./PublicMasterProfileStore";
import { AnalyticsSource, withSource } from "../../AnalyticsClient/AnalyticsSources";
import { AnalyticsClient } from "../../AnalyticsClient/AnalyticsClient";
import { BottomSheet } from "../../GloablComponents/BottomSheet/BottomSheet";
import { DelayedLookstyLoader } from "../../GloablComponents/Loader/DelayedLookstyLoader";

const formatReviewDate = (value: string) => {
  return new Date(value).toLocaleDateString("ru-RU");
};

const formatDistanceLabel = (distanceMeters?: number | null, distanceText?: string | null) => {
  const text = distanceText?.trim();

  if (text) {
    return text.replace(/\s+от вас$/i, "");
  }

  if (typeof distanceMeters !== "number" || !Number.isFinite(distanceMeters) || distanceMeters < 0) {
    return "";
  }

  if (distanceMeters < 1000) {
    return `${Math.round(distanceMeters)} м`;
  }

  const kilometers = distanceMeters / 1000;

  return `${kilometers.toLocaleString("ru-RU", {
    minimumFractionDigits: kilometers < 10 ? 1 : 0,
    maximumFractionDigits: 1,
  })} км`;
};

export const PublicMasterProfilePage = observer(() => {
  const store = useMemo(() => new PublicMasterProfileStore(), []);
  const navigate = useNavigate();
  const params = useParams();

  const loadMorePostsRef = useRef<HTMLDivElement | null>(null);
  const loadMoreReviewsRef = useRef<HTMLDivElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const profileAnalyticsSentRef = useRef(false);

  const source = searchParams.get("source") || AnalyticsSource.PublicMasterProfile;
  const [isContactsOpen, setIsContactsOpen] = useState(false);

  const bookingActionRef = useRef<HTMLElement | null>(null);
  const [isStickyBookingVisible, setIsStickyBookingVisible] = useState(false);

  useEffect(() => {
    const id = Number(params.id);
    if (id) store.load(id);
  }, [params.id, store]);

  useEffect(() => {
    const tab = searchParams.get("tab");

    if (tab === "works" || tab === "services" || tab === "reviews") {
      store.setActiveTab(tab);
    }
  }, [searchParams, store]);

  useEffect(() => {
    if (store.ActiveTab !== "reviews") return;
    if (!store.Master) return;
    if (store.Reviews.length) return;
    if (store.IsLoadingReviews) return;

    store.loadReviews(true);
  }, [store, store.ActiveTab, store.Master?.id, store.Reviews.length]);

  useEffect(() => {
    if (!store.Master) return;
    if (profileAnalyticsSentRef.current) return;

    profileAnalyticsSentRef.current = true;

    AnalyticsClient.track({
      eventType: "master_profile_opened",
      source: searchParams.get("source") || AnalyticsSource.Direct,
      entityType: "master",
      entityId: store.Master.id,
    });
  }, [store.Master, searchParams]);

  useEffect(() => {
    if (!store.Master) {
      setIsStickyBookingVisible(false);
      return;
    }

    const element = bookingActionRef.current;

    if (!element) {
      setIsStickyBookingVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStickyBookingVisible(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [store.Master?.id]);

  const handleBookingClick = () => {
    if (store.Master?.hasSchedule) {
      navigate(withSource(`/master/${store.Master.id}/booking`, AnalyticsSource.PublicMasterProfile));
      return;
    }

    navigate(withSource(`/master/${store.Master?.id}/booking-request`, AnalyticsSource.PublicMasterProfile));
  };

  const getTelegramUrl = (value?: string) => {
    const telegram = value?.trim();

    if (!telegram) return "";

    if (telegram.startsWith("http://") || telegram.startsWith("https://")) {
      return telegram;
    }

    return `https://t.me/${telegram.replace("@", "")}`;
  };

  useEffect(() => {
    if (store.ActiveTab !== "works") return;
    if (!store.HasMorePosts) return;

    const element = loadMorePostsRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          store.loadMorePosts();
        }
      },
      {
        root: null,
        rootMargin: "300px",
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [store, store.ActiveTab, store.HasMorePosts, store.Posts.length]);

  const trackTelegramClick = () => {
    if (!store.Master) return;

    AnalyticsClient.track({
      eventType: "telegram_opened",
      source,
      entityType: "master",
      entityId: store.Master.id,
      metadata: {
        telegram: store.Master.telegram,
      },
    });
  };

  useEffect(() => {
    if (store.ActiveTab !== "reviews") return;
    if (!store.HasMoreReviews) return;

    const element = loadMoreReviewsRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          store.loadReviews(false);
        }
      },
      {
        root: null,
        rootMargin: "300px",
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [store, store.ActiveTab, store.HasMoreReviews, store.Reviews.length]);

  if (store.IsLoading) {
    return (
      <main className="public-master-page">
        <DelayedLookstyLoader variant="master" title="Загружаем мастера" subtitle="Готовим профиль, работы и отзывы" show={store.IsLoading} />
      </main>
    );
  }

  if (store.Error) {
    return <div className="public-master-message error">{store.Error}</div>;
  }

  if (!store.Master) return null;
  const telegramUrl = getTelegramUrl(store.Master.telegram);

  const masterLocation = [store.Master.location, store.Master.address]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(", ");

  const masterDistanceText = store.Master.distanceText?.trim() ?? "";
  const masterDistanceLabel = formatDistanceLabel(store.Master.distanceMeters, store.Master.distanceText);

  return (
    <>
      <BottomSheet isOpen={isContactsOpen} onClose={() => setIsContactsOpen(false)} className="public-master-contacts-sheet">
        <div className="public-master-contacts-head">
          <h2>Контакты мастера</h2>

          <button type="button" onClick={() => setIsContactsOpen(false)}>
            <X size={22} />
          </button>
        </div>

        <div className="public-master-contacts-list">
          <div>
            <span>Адрес</span>
            <strong>{masterLocation || "Не указан"}</strong>
          </div>

          {masterDistanceText && (
            <div>
              <span>Расстояние</span>
              <strong className="public-master-contacts-distance">
                <Navigation size={17} />
                {masterDistanceText}
              </strong>
            </div>
          )}

          {telegramUrl ? (
            <a href={telegramUrl} target="_blank" rel="noreferrer" onClick={trackTelegramClick}>
              <span>Telegram</span>
              <strong>
                <Send size={17} />
                {store.Master.telegram}
              </strong>
            </a>
          ) : (
            <div>
              <span>Telegram</span>
              <strong>Не указан</strong>
            </div>
          )}
        </div>
      </BottomSheet>
      <main className="public-master-page">
        <header className="public-master-topbar">
          <button onClick={() => navigate(-1)}>
            <ArrowLeft size={27} />
          </button>

          <button onClick={() => setIsContactsOpen(true)}>
            <ContactRound size={24} />
          </button>
        </header>

        <section className="public-master-hero">
          <div className="public-master-identity">
            <div className="public-master-avatar">
              {store.Master.avatarUrl ? <img src={BaseService.GetFileUrl(store.Master.avatarUrl)} alt={store.Master.name} /> : <span>{store.Master.name[0]}</span>}
            </div>

            <div className="public-master-info">
              <h1>{store.Master.name}</h1>

              <div className="public-master-meta">
                {store.Rating && (
                  <span className="public-master-rating">
                    <Star size={14} />
                    {store.Rating.averageRating.toFixed(1)} · {store.Rating.reviewsCount} отзывов
                  </span>
                )}

                {masterDistanceLabel && (
                  <span className="public-master-distance" title={masterDistanceText || masterDistanceLabel}>
                    <Navigation size={14} />
                    {masterDistanceLabel}
                  </span>
                )}
              </div>

              {masterLocation && (
                <button type="button" className="public-master-location" onClick={() => setIsContactsOpen(true)} title={masterLocation}>
                  <MapPin size={14} />
                  <span>{masterLocation}</span>
                </button>
              )}
            </div>
          </div>

          {store.Master.description && <p className="public-master-description">{store.Master.description}</p>}
        </section>

        <section ref={bookingActionRef} className="public-master-actions">
          <button className="public-master-book-button" onClick={handleBookingClick}>
            <CalendarDays size={19} />
            <span>{store.Master?.hasSchedule ? "Записаться" : "Оставить заявку"}</span>
          </button>
        </section>

        <div className="public-master-categories">
          <button
            className={store.ActiveTab === "works" ? "active" : ""}
            onClick={() => {
              store.setActiveTab("works");
              setSearchParams({ tab: "works" });
            }}
          >
            Работы
          </button>

          <button
            className={store.ActiveTab === "services" ? "active" : ""}
            onClick={() => {
              store.setActiveTab("services");
              setSearchParams({ tab: "services" });
            }}
          >
            Услуги
          </button>

          <button
            className={store.ActiveTab === "reviews" ? "active" : ""}
            onClick={() => {
              store.setActiveTab("reviews");
              setSearchParams({ tab: "reviews" });
            }}
          >
            Отзывы
          </button>
        </div>

        {store.ActiveTab === "works" && (
          <>
            <section className="public-master-posts">
              {store.Posts.map((post) => (
                <button key={post.id} className="public-master-post-card" onClick={() => navigate(`/feed/${post.id}`)}>
                  <img src={BaseService.GetFileUrl(post.imageUrl)} alt={post.title} />
                </button>
              ))}
            </section>

            {store.Posts.length === 0 && <div className="public-master-empty">У мастера пока нет работ</div>}

            {store.HasMorePosts && (
              <div ref={loadMorePostsRef} className="public-master-scroll-loader">
                {store.IsLoadingPosts ? "Загружаем..." : ""}
              </div>
            )}
          </>
        )}

        {store.ActiveTab === "services" && (
          <section className="public-master-services">
            {store.Services.length ? (
              store.Services.map((service) => (
                <article className="public-master-service-card" key={service.id}>
                  <div className="public-master-service-card-main">
                    <div className="public-master-service-icon">
                      <Scissors size={20} />
                    </div>

                    <div>
                      <h3>{service.name}</h3>
                      <p>{service.durationMinutes} мин</p>
                    </div>
                  </div>

                  <div className="public-master-service-card-bottom">
                    <span className="public-master-service-price">{service.price.toLocaleString("ru-RU")} ₽</span>

                    {service.bookingsCount > 10 && <span className="public-master-service-bookings">{service.bookingsCount} заказов</span>}
                  </div>
                </article>
              ))
            ) : (
              <div className="public-master-empty">У мастера пока нет услуг</div>
            )}
          </section>
        )}

        {store.ActiveTab === "reviews" && (
          <section className="public-master-reviews">
            <div className="public-master-review-filters">
              <button className={store.SelectedReviewServiceId === null ? "active" : ""} onClick={() => store.setReviewService(null)}>
                Все
              </button>

              {store.Services.map((service) => (
                <button key={service.id} className={store.SelectedReviewServiceId === service.id ? "active" : ""} onClick={() => store.setReviewService(service.id)}>
                  {service.name}
                </button>
              ))}
            </div>

            {store.IsLoadingReviews && !store.Reviews.length && <div className="public-master-empty">Загружаем отзывы...</div>}

            {!store.IsLoadingReviews && !store.Reviews.length && <div className="public-master-empty">Отзывов пока нет</div>}

            {!!store.Reviews.length && (
              <div className="public-master-review-list">
                {store.Reviews.map((review) => {
                  const service = review.serviceId ? store.Services.find((item) => item.id === review.serviceId) : null;

                  return (
                    <article className="public-master-review-card" key={review.id}>
                      <div className="public-master-review-top">
                        <div>
                          <h3>{store.getReviewAuthor(review)}</h3>
                          <span>{formatReviewDate(review.createdAt)}</span>
                        </div>

                        <b className="public-master-review-rating">
                          <Star size={14} />
                          {review.rating}
                        </b>
                      </div>

                      {service && (
                        <div className="public-master-review-service-card">
                          <div>
                            <Scissors size={17} />
                          </div>

                          <section>
                            <strong>{service.name}</strong>
                            <span>
                              {service.price.toLocaleString("ru-RU")} ₽ · {service.durationMinutes} мин
                            </span>
                          </section>
                        </div>
                      )}

                      {review.text && <p>{review.text}</p>}
                    </article>
                  );
                })}
              </div>
            )}

            {store.HasMoreReviews && (
              <div ref={loadMoreReviewsRef} className="public-master-scroll-loader">
                {store.IsLoadingReviews ? "Загружаем..." : ""}
              </div>
            )}
          </section>
        )}

        {isStickyBookingVisible && (
          <div className="public-master-sticky-booking">
            <button type="button" onClick={handleBookingClick}>
              <CalendarDays size={19} />
              <span>{store.Master?.hasSchedule ? "Записаться" : "Оставить заявку"}</span>
            </button>
          </div>
        )}
      </main>
    </>
  );
});
